/**
 * Form-encoding helper for the ff-api admin endpoints that read
 * `await request.form` and therefore ignore a JSON body entirely.
 *
 * Why this is needed
 * ------------------
 * `clientStore.request()` JSON-stringifies any plain object, and the Fastify
 * proxy (`src/server/lib/proxyCore.ts:164`) forwards it as `application/json`
 * whenever the incoming content-type is JSON. A form-only backend handler then
 * sees an empty dict and either raises `ValueError` or no-ops.
 *
 * Sending a `FormData` instance takes the other branch the whole way down:
 *   client FormData → multipart fetch → `req.isMultipart()` in proxyCore
 *   → rebuilt FormData → Quart `request.form` parses it.
 *
 * Endpoints that REQUIRE this (verified against ff-api):
 *   POST   /api/admin/users
 *   POST   /api/admin/role_type
 *   PUT    /api/admin/role_type/<uartid>
 *   POST   /api/admin/permissions
 *   PUT    /api/admin/permissions/<permid>
 *   PUT    /api/admin/user_role/<uarid>
 *   POST   /api/admin/organizations
 *
 * Tracked as gap C-1 in docs/BACKEND_GAPS_users_orgs.md — once the backend
 * accepts JSON on these, callers can drop `toFormData` and pass plain objects.
 */
export const toFormData = (payload: object): FormData => {
  const form = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    // Quart's `request.form` is flat — objects have to be serialized.
    form.append(
      key,
      typeof value === 'object' ? JSON.stringify(value) : String(value)
    );
  });

  return form;
};
